import React, { useEffect, useState } from "react";
import Styles from "./Element.module.css";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";
import SelectInput from "../../../../commons/Inputs/Select/SelectInput";
import { useSelector } from "react-redux";
import DateInput from "../../../../commons/Inputs/DateInput/DateInput";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { verifyCompanyDisplayName } from "../../../../Services/ApiCalling/Auth";
import { decryptData } from "../../../../commons/Decode";

const CompanyDetails = ({ userData, handleData, handleProceed }) => {
  const publicData = useSelector((state) => state?.publicSlice);

  const initialState = {
    holdingCompany:
      userData?.holdingCompany?.id || userData?.holdingCompany || "",
    traderType: userData?.traderType || {},
    companyName: userData?.companyName || "",
    displayName: userData?.displayName || "",
    companyDescription: userData?.companyDescription || "",
    category: userData?.category || {},
    registration: userData?.registration || "",
    pan: userData?.pan || "",
    tan: userData?.tan || "",
    gst: userData?.gst || "",
    license: userData?.license || "",
    bankName: userData?.bankName || "",
    bankAddress: userData?.bankAddress || "",
    accountNo: userData?.accountNo || "",
    ifsc: userData?.ifsc || "",
    rating: userData?.rating || {},
    ratingYear: userData?.ratingYear || "",
    agency: userData?.agency || "",
    byRules: false,
    byLaw: false,
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    const newErrors = { ...errors };

    const fieldDependencies = {
      displayName: "companyName",
      companyDescription: "displayName",
      holdingCompany: "companyDescription",
      category: "holdingCompany",
      registration: "category",
      tan: "registration",
      gst: "tan",
      pan: "gst",
      license: "pan",
      bankName: "license",
      bankAddress: "bankName",
      accountNo: "bankAddress",
      ifsc: "accountNo",
      rating: "ifsc",
      ratingYear: "rating",
      agency: "ratingYear",
    };

    if (value) {
      delete newErrors[field];
    }

    const dependentField = fieldDependencies[field];
    if (dependentField && !formData[dependentField]) {
      newErrors[dependentField] = `${dependentField
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())} is required`;
    }

    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      return newData;
    });
    setErrors(newErrors);
  };

  const validateFields = async () => {
    const requiredFields = [
      "companyName",
      "displayName",
      "companyDescription",
      "registration",
      "pan",
      "tan",
      "gst",
      "bankName",
      "bankAddress",
      "accountNo",
      "ifsc",
      "traderType",
    ];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase())} is required`;
      }
    });

    if (formData?.displayName) {
      try {
        const res = await verifyCompanyDisplayName(formData?.displayName);
        const decryptedData = decryptData(res?.data);

        if (
          decryptedData &&
          (decryptedData?.code !== 200 || decryptedData?.success === false)
        ) {
          newErrors.displayName =
            decryptedData?.message ||
            decryptedData?.error ||
            "Display name not available";
        }
      } catch (err) {
        newErrors.displayName = "Could not verify display name";
      }
    }

    if (
      !formData.holdingCompany ||
      (!formData.holdingCompany?.id &&
        typeof formData.holdingCompany !== "string")
    ) {
      newErrors.holdingCompany = "Holding Company is required";
    }

    if (!formData.category?.id) {
      newErrors.category = "Category is required";
    }

    if (!formData.ratingYear) {
      newErrors.ratingYear = "Credit Rating Year is required";
    }

    if (!formData.agency) {
      newErrors.agency = "Credit Rating Agency is required";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Password does not match";
    }
    if (!formData.rating?.id) {
      newErrors.rating = "Rating is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async (data) => {
    const isValid = await validateFields();
    if (!isValid) return;
    const userCompany = {
      ...formData,
      roles: userData?.profile?.value,
      holdingCompanyId: formData.holdingCompany?.id || formData.holdingCompany,
      categoryId: formData.category?.id || formData.category,
      bankAccounts: {
        nameAddress: `${formData.bankName} ${formData.bankAddress}`,
        accNo: formData.accountNo,
        ifsc: formData.ifsc,
        operationalAt: "",
      },
      creditRatingId: formData.rating?.id || formData.rating,
      creditRatingYear: formData.ratingYear,
      creditRatingAgency: formData.agency,
      isAcceptedByLaws: formData.byLaw,
      isAcceptedBusinessRule: formData.byRules,
    };

    handleData("userCompany", userCompany);
    handleProceed(data);
  };

  const renderTextInput = (
    field,
    heading,
    example,
    type = "text",
    readOnly = false
  ) => (
    <TextInput
      heading={`${heading} `}
      value={formData[field]}
      onChange={(value) => handleChange(field, value)}
      example={example}
      type={type}
      height="45"
      error={errors[field]}
      readOnly={readOnly}
    />
  );

  const renderSelectInput = (field, heading, list, example) => (
    <SelectInput
      onChange={(value) => handleChange(field, value)}
      list={list}
      value={formData[field]}
      example={example}
      heading={`${heading} `}
      height="45px"
      error={errors[field]}
    />
  );

  const renderDateInput = (field, heading, maxDate) => (
    <DateInput
      heading={`${heading} `}
      value={formData[field]}
      onChange={(value) => handleChange(field, value)}
      maxDate={maxDate}
      height="45"
      error={errors[field]}
    />
  );

  const renderCheckbox = (label, field) => {
    const handleCheckboxClick = () => {
      handleChange(field, !formData[field]);
    };

    const handleLabelClick = (e) => {
      e.stopPropagation();

      if (field === "byLaw") {
        const teamsUrl =
          "https://gna-general-files.s3.ap-south-1.amazonaws.com/Website+Assets+PDF/GNA-bye-laws.pdf";
        window.open(teamsUrl, "_blank");
      } else if (field === "byRules") {
        const teamsUrl =
          "https://gna-general-files.s3.ap-south-1.amazonaws.com/Website+Assets+PDF/GNA-business-rules.pdf";
        window.open(teamsUrl, "_blank");
      }
    };

    return (
      <div className={Styles.checkSelction} onClick={handleCheckboxClick}>
        {formData[field] ? (
          <CheckBoxIcon style={{ fontSize: "24px", color: "#f1935c" }} />
        ) : (
          <CheckBoxOutlineBlankIcon
            style={{ fontSize: "24px", color: "#6f6d7c" }}
          />
        )}
        <div>
          <span>I Understand and Accept the</span>
          <span
            style={{
              color: "#f1935c",
              paddingLeft: "8px",
              cursor:
                field === "byLaw" || field === "byRules"
                  ? "pointer"
                  : "default",
            }}
            onClick={
              field === "byLaw" || field === "byRules"
                ? handleLabelClick
                : undefined
            }
          >
            {label}
          </span>
          {(field === "byLaw" || field === "byRules") && (
            <span
              style={{ color: "#666", fontSize: "12px", display: "block" }}
            ></span>
          )}
        </div>
      </div>
    );
  };

  const renderCheckboxTrader = (field, options, heading) => (
    <div className={Styles.checkBoxElem}>
      <div className={Styles.checkBoxHead}>{heading}</div>
      <div
        className={`${Styles.selectCheckElemEent} ${Styles.checkboxGenerator}`}
      >
        {(Array.isArray(options) ? options : []).map((option) => (
          <div
            key={option.value}
            onClick={() => handleChange(field, option.value)}
            style={{ width: `${100 / options.length}%` }}
            className={
              formData[field] === option.value
                ? Styles.activeCheck
                : Styles.inactiveCheck
            }
          >
            {option.label}
          </div>
        ))}
      </div>
      {errors[field] && (
        <span className={Styles.errorPass}>{errors[field]}</span>
      )}
    </div>
  );

  useEffect(() => {
    // Only update if userData.userCompany actually exists and has changed
    if (userData?.userCompany) {
      setFormData((prevData) => ({
        holdingCompany:
          userData?.userCompany?.holdingCompany?.id ||
          userData?.userCompany?.holdingCompany ||
          prevData.holdingCompany ||
          "",
        traderType:
          userData?.userCompany?.traderType || prevData.traderType || {},
        companyName:
          userData?.userCompany?.companyName || prevData.companyName || "",
        displayName:
          userData?.userCompany?.displayName || prevData.displayName || "",
        companyDescription:
          userData?.userCompany?.companyDescription ||
          prevData.companyDescription ||
          "",
        category: userData?.userCompany?.category || prevData.category || {},
        registration:
          userData?.userCompany?.registration || prevData.registration || "",
        pan: userData?.userCompany?.pan || prevData.pan || "",
        tan: userData?.userCompany?.tan || prevData.tan || "",
        gst: userData?.userCompany?.gst || prevData.gst || "",
        license: userData?.userCompany?.license || prevData.license || "",
        bankName: userData?.userCompany?.bankName || prevData.bankName || "",
        bankAddress:
          userData?.userCompany?.bankAddress || prevData.bankAddress || "",
        accountNo: userData?.userCompany?.accountNo || prevData.accountNo || "",
        ifsc: userData?.userCompany?.ifsc || prevData.ifsc || "",
        rating: userData?.userCompany?.rating || prevData.rating || {},
        ratingYear:
          userData?.userCompany?.ratingYear || prevData.ratingYear || "",
        agency: userData?.userCompany?.agency || prevData.agency || "",
        byRules: userData?.userCompany?.byRules || prevData.byRules || false,
        byLaw: userData?.userCompany?.byLaw || prevData.byLaw || false,
      }));
    }
  }, [userData?.userCompany]);

  return (
    <div className={Styles.elementMain}>
      <div className={Styles.displayInputSection}>
        <div className={Styles.conterntHeader}>Company Details</div>
        <div className={Styles.detailsInpMainContainer}>
          <div className={Styles.layer3ElementsContainer}>
            {renderTextInput("companyName", "Company Name", "Eg. GNA Energy")}
            {renderTextInput("displayName", "Company Display Name", "Eg. GNA")}
            {renderTextInput(
              "companyDescription",
              "Company Description",
              "Eg. India's Premier OTC Platform for Renewable Energy, Battery Energy, Storage Systems (BESS)"
            )}
          </div>
          <div className={Styles.layer1ElementsContainer}></div>
          <div className={Styles.layer3ElementsContainer}>
            {renderSelectInput(
              "holdingCompany",
              "Holding Company",
              publicData?.holdingCompany,
              "Eg. Select Holding Company"
            )}
            {renderSelectInput(
              "category",
              "Category",
              publicData?.category,
              "Eg. Select Category"
            )}
            {renderDateInput(
              "registration",
              "Date of Incorporation/Registration",
              new Date()
            )}
          </div>
          <div className={Styles.layer3ElementsContainer}>
            {renderTextInput("tan", "TAN Number", "Enter TAN Number")}
            {renderTextInput("gst", "GST Number", "Enter GST Number")}
            {renderTextInput("pan", "PAN Number", "Enter PAN Number")}
          </div>
          {userData?.profile?.case === "trader" && (
            <div className={Styles.layer3ElementsContainer}>
              {renderTextInput(
                "license",
                "Trading License Number",
                "Enter Trading License"
              )}
              {renderCheckboxTrader(
                "traderType",
                [
                  { value: true, label: "InterState" },
                  { value: false, label: "IntraState" },
                ],
                "Trader Type"
              )}
              <div></div>
            </div>
          )}
          <div className={Styles.divider}></div>
          <div className={Styles.subConterntHeader}>Company Bank Details</div>
          <div className={Styles.layer3ElementsContainer}>
            {renderTextInput("bankName", "Bank Name", "Enter Bank Name")}
            {renderTextInput(
              "accountNo",
              "Account Number",
              "Enter Account Number"
            )}
            {renderTextInput("ifsc", "IFSC Code", "Enter IFSC Code")}
          </div>
          <div className={Styles.layer1ElementsContainer}>
            {renderTextInput(
              "bankAddress",
              "Branch Address",
              "Enter Branch Address"
            )}
          </div>
          <div className={Styles.divider}></div>
          <div className={Styles.layer3ElementsContainer}>
            {renderSelectInput(
              "rating",
              "Credit Rating",
              publicData?.publicRating,
              "Eg. Select Credit Rating"
            )}
            {renderTextInput(
              "ratingYear",
              "Credit Rating Year",
              "Eg. FEB 2025"
            )}
            {renderTextInput(
              "agency",
              "Credit Rating Agency",
              "Eg. GNA Energy"
            )}
          </div>
          <div className={Styles.divider}></div>
          {renderCheckbox("Business Rules", "byRules")}
          {renderCheckbox("Bye Laws", "byLaw")}
        </div>
      </div>
      <div className={Styles.buttonSection}>
        <div className={Styles.prevBtn} onClick={() => handleProceed("prev")}>
          Back
        </div>
        <div className={Styles.nextBtn} onClick={() => handleContinue("next")}>
          Next
        </div>
      </div>
    </div>
  );
};

export default CompanyDetails;
